import * as React from 'react';
import { Fragment } from 'react';

/** Material UI Packages. */
import { Checkbox, Typography, Autocomplete, TextField, Popper } from '@mui/material';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { incotermList } from 'shared/config/constantsData';
import {
    CustomAutocompleteCell_CodeName,
    CustomAutocompleteCell_TradePartner,
    DateTypingEditCell,
} from 'shared/ui/RenderCell';
import CustomDetailPanelToggle from 'shared/ui/CustomDetailPanelToggle';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import { parsePastedDate, createNameFilterOperators } from 'shared/lib/datagridHelpers';
import { TeamEditCell } from 'shared/ui/TeamCell';
import {
    actionsCol,
    progressCol,
    statusCol,
    renderHazmatCell,
    truckerCol,
    truckerLinesByShipment,
} from './CommonMainDef';
import GoogleRenderCell from 'shared/ui/GoogleRenderCell';
import { createQuickFilterFn } from 'shared/lib/quickFilterUtils';

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '11px',
    },
});

/** Returns the HBL alias rows for a row ordered by their parent HBL's order (not by creation order). */
const getAliasesOrderedByHbl = (rowId, hblShipmentList, hblAliasShipmentList) => {
    const hblIds = hblShipmentList.reduce((acc, item) => {
        if (item.il_air_export_id === rowId) {
            acc.push(item.id);
        }
        return acc;
    }, []);
    return hblIds.flatMap((hblId) => hblAliasShipmentList.filter((item) => item.il_air_export_hbl_id === hblId));
};

const hasHazmat = (rows) => rows.some((item) => item.hazmat === true);

/** Define Air Export Datagrid Fields. */
export const getColumns = (
    shipmentList,
    hblShipmentList,
    hblAliasShipmentList,
    splitShipmentList,
    commentList,
    selectValue,
    statusList,
    tradePartnerOptions,
    locationOptions,
    vesselOptions,
    handleSelectionChange,
    handleEmailTemplateOpen,
    handleCommentOpen,
    handleHistoryOpen,
    handleTradePartnerChange,
    handleLocationChange,
    handleVesselChange,
    shipmentRow,
    setShipmentRow,
    todoSummary,
    tabValue
) => [
    {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        minWidth: 25,
        width: 30,
        renderCell: (params) => <CustomDetailPanelToggle id={params.id} />,
    },
    {
        field: 'selection',
        headerName: '',
        minWidth: 25,
        width: 30,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (params) => {
            if (params.rowNode.type === 'group') {
                return '';
            }
            return (
                <Checkbox
                    checked={params.id === selectValue || false}
                    onChange={() => handleSelectionChange(params.id)}
                    sx={customized_style.itemDatagrid_Typography}
                />
            );
        },
    },
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        width: 30,
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    {
        field: 'il_team_id',
        headerName: 'Team',
        width: 75,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        // Team reads back as { id, name } like Client (OS-566); the grid shows, sorts, filters and searches the name.
        valueGetter: (params) => params,
        valueFormatter: (params) => params?.name ?? '',
        groupingValueGetter: (value) => value?.name || '',
        renderCell: (params) => (
            <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
        ),
        renderEditCell: (params) => <TeamEditCell params={params} />,
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_opus_reference_id',
        headerName: 'Opus Ref',
        align: 'center',
        headerAlign: 'center',
        width: 75,
        editable: true,
        headerClassName: 'custom-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    ...actionsCol({
        tabValue,
        commentList,
        handleEmailTemplateOpen,
        handleCommentOpen,
        handleHistoryOpen
    }),
    {
        field: 'incoterm',
        headerName: 'Incoterms',
        width: 75,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => (
            <Autocomplete
                disableClearable
                disablePortal
                openOnFocus
                options={incotermList}
                onChange={(event, newValue) => {
                    params.api.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: newValue,
                    });
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        autoFocus
                        sx={{
                            px: 0.5,
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                value={shipmentList.filter((item) => item.id === params.id)[0].incoterm || ''}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                }}
            />
        ),
    },
    {
        field: 'il_trade_partner_id_client',
        headerName: 'Client',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.name : '';
        },
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_TradePartner
                    params={params}
                    options={tradePartnerOptions}
                    handleInputChange={handleTradePartnerChange}
                />
            );
        },
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_trade_partner_id_shipper',
        headerName: 'Shipper',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            const result = params?.map((item) => item?.name || '');
            return result;
        },
        groupingValueGetter: (value) => {
            const result = value?.map((item) => item?.name || '');
            return result || '';
        },
        renderCell: (params) => {
            const arr = params.value || [];
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {arr.map((item, index) => (
                        <Fragment key={index}>
                            {item || ''}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            );
        },
    },
    {
        field: 'il_trade_partner_id_consignee',
        headerName: 'Consignee',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            const result = params?.map((item) => item?.name || '');
            return result;
        },
        groupingValueGetter: (value) => {
            const result = value?.map((item) => item?.name || '');
            return result || '';
        },
        renderCell: (params) => {
            const arr = params.value || [];
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {arr.map((item, index) => (
                        <Fragment key={index}>
                            {item || ''}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            );
        },
    },
    {
        field: 'il_trade_partner_id_forwarder',
        headerName: 'Forwarder',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.name : '';
        },
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_TradePartner
                    params={params}
                    options={tradePartnerOptions}
                    handleInputChange={handleTradePartnerChange}
                />
            );
        },
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'booking_number',
        headerName: 'Booking',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    {
        field: 'mawb',
        headerName: 'MAWB',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    {
        field: 'hawb',
        headerName: 'HAWB',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            const result = params?.filter((item) => item !== '').join(', ') || '';
            return result;
        },
        renderCell: (params) => {
            const matchedHBL = hblShipmentList.filter((hblItem) => hblItem.il_air_export_id === params.row.id);
            const result = matchedHBL.map((item) => item.hawb).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'alias',
        headerName: 'Alias',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            const result = params?.filter((item) => item !== '').join(', ') || '';
            return result;
        },
        renderCell: (params) => {
            const findContainerArray = getAliasesOrderedByHbl(params.row.id, hblShipmentList, hblAliasShipmentList);
            const result = findContainerArray.map((item) => item.alias).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'il_opus_vessel_id',
        headerName: 'Flight#',
        align: 'center',
        headerAlign: 'center',
        width: 100,
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const findContainerArray = getAliasesOrderedByHbl(params.row.id, hblShipmentList, hblAliasShipmentList);
            const result = findContainerArray.map((item) => item.il_opus_vessel_id).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'quantity',
        headerName: 'Vol',
        width: 60,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            const result = params?.filter((item) => item !== '').join(', ') || '';
            return result;
        },
        renderCell: (params) => {
            const findContainerArray = getAliasesOrderedByHbl(params.row.id, hblShipmentList, hblAliasShipmentList);
            const result = findContainerArray.map((item) => item.quantity).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'il_opus_volume_unit_id',
        headerName: 'Unit',
        width: 70,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const findContainerArray = getAliasesOrderedByHbl(params.row.id, hblShipmentList, hblAliasShipmentList);
            const result = findContainerArray.map((item) => item.il_opus_volume_unit_id).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'haz',
        headerName: 'HAZ',
        width: 50,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (value, row) => Boolean(row && hasHazmat(getAliasesOrderedByHbl(row.id, hblShipmentList, hblAliasShipmentList))),
        type: 'boolean',
        renderCell: renderHazmatCell,
    },
    {
        field: 'weight',
        headerName: 'Weight',
        width: 80,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            const result = params?.filter((item) => item !== '' && item != null).join(', ') || '';
            return result;
        },
        renderCell: (params) => {
            const findContainerArray = getAliasesOrderedByHbl(params.row.id, hblShipmentList, hblAliasShipmentList);
            const result = findContainerArray
                .map((item) => (item.weight != null && item.weight !== '' ? `${item.weight} kg` : ''))
                .join('\n');
            return result.trim() ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'il_location_id_pick_up',
        headerName: 'Pick Up',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            if (!params) return '';
            const result = params?.filter((item) => item !== '' && item != null).join(', ') || '';
            return result;
        },
        renderCell: (params) => {
            const findContainerArray = getAliasesOrderedByHbl(params.row.id, hblShipmentList, hblAliasShipmentList);
            const result = findContainerArray.map((item) => item.il_location_id_pick_up || '').join('\n');
            return result.trim() ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'place_of_receipt',
        headerName: 'POR',
        width: 150,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => (
            <GoogleRenderCell params={params} shouldFocus={true} hasRowUpdate={true} />
        ),
    },
    {
        field: 'il_location_id_aol',
        headerName: 'AOL',
        width: 100,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.code : '';
        },
        groupingValueGetter: (value) => {
            return value?.code || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.code || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_CodeName
                    params={params}
                    options={locationOptions}
                    handleInputChange={(event, value) => handleLocationChange(event, value)}
                />
            );
        },
        filterOperators: createNameFilterOperators('code'),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_location_id_aod',
        headerName: 'AOD',
        width: 100,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.code : '';
        },
        groupingValueGetter: (value) => {
            return value?.code || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.code || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_CodeName
                    params={params}
                    options={locationOptions}
                    handleInputChange={(event, value) => handleLocationChange(event, value)}
                />
            );
        },
        filterOperators: createNameFilterOperators('code'),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_location_id_faod',
        headerName: 'F.DEST',
        width: 100,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.code : '';
        },
        groupingValueGetter: (value) => {
            return value?.code || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.code || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_CodeName
                    params={params}
                    options={locationOptions}
                    handleInputChange={(event, value) => handleLocationChange(event, value)}
                />
            );
        },
        filterOperators: createNameFilterOperators('code'),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    truckerCol({
        linesByShipmentId: truckerLinesByShipment({
            hblList: hblShipmentList,
            hblParentKey: 'il_air_export_id',
            childList: hblAliasShipmentList,
            childParentKey: 'il_air_export_hbl_id',
        }),
    }),
    {
        field: 'reference',
        headerName: 'Reference',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            const result = params?.filter((item) => item !== '').join(', ') || '';
            return result;
        },
        renderCell: (params) => {
            const matchedHBL = hblShipmentList.filter((hblItem) => hblItem.il_air_export_id === params.row.id);
            const result = matchedHBL.map((item) => item.reference).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },

    {
        field: 'doc_cut',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'Doc Cut',
        width: 70,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'port_cut',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'Port Cut',
        width: 75,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'etd',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'ETD',
        width: 70,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'eta',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'ETA',
        width: 70,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'feta',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'F.ETA',
        width: 70,
        editable: true,
        required: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    ...progressCol({
        tabValue,
        todoSummary,
        commentList,
        width: 162
    }),
    ...statusCol({
        statusList,
        tabValue,
        width: 248
    }),
    /** QuickFilter only: `row.__quick_search`는 ShipmentMainTable `shipmentRow`에서 채움 */
    {
        field: '__quick_search',
        headerName: '',
        width: 0,
        minWidth: 0,
        maxWidth: 0,
        sortable: false,
        disableReorder: true,
        getApplyQuickFilterFn: createQuickFilterFn,
        renderCell: () => null,
    },
];
